import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Box, Button,
  Container, Card, CardContent, Stack, TextField, FormControl,
  Select, MenuItem, CircularProgress, Snackbar, Alert, Chip,
  Divider, Avatar,
} from '@mui/material';
import { styled }    from '@mui/material/styles';
import { Slide }     from '@mui/material';
import CloseIcon         from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import HistoryIcon       from '@mui/icons-material/History';
import PersonOffIcon     from '@mui/icons-material/PersonOff';
import ErrorOutlineIcon  from '@mui/icons-material/ErrorOutline';
import SwapHorizIcon     from '@mui/icons-material/SwapHoriz';
import SearchableDropdown from "../../pages/SearchableDropdown";
import { assetService } from "../../services/assetService";

import {
  getAssignmentTypeConfig,
  getSortedAssignmentHistory,
  validateAssignmentForm,
  buildAssignPayload,
  getAssignmentSuccessMessage,
  buildReleasePayload,
  ASSIGNMENT_TYPES,
} from "../../utils/assetUtils";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (val) => {
  if (!val) return '—';
  let d;
  if (typeof val?.toDate === 'function') d = val.toDate();
  else if (val?.seconds !== undefined)   d = new Date(val.seconds * 1000);
  else                                   d = new Date(val);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getInitials = (name = '') =>
  name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '??';

// ─────────────────────────────────────────────────────────────────────────────
// SLIDE TRANSITION
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b',
  marginBottom: '1.25rem', paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem', fontWeight: 600, color: '#374151',
  textTransform: 'uppercase', letterSpacing: '0.025em',
  marginBottom: '0.375rem',
}));

const FormGroup = styled(Box)(() => ({
  flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    '& fieldset':        { borderColor: '#d1d5db' },
    '&:hover fieldset':  { borderColor: '#3b82f6' },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      boxShadow:   '0 0 0 3px rgba(59,130,246,0.12)',
    },
  },
}));

const FormRow = styled(Box)(() => ({
  display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap',
  '&:last-child': { marginBottom: 0 },
}));

const HistoryEntry = styled(Box)(({ entrytype }) => {
  const c = getAssignmentTypeConfig(entrytype);
  return {
    display: 'flex', gap: '1rem', alignItems: 'flex-start',
    padding: '1rem', borderRadius: '8px',
    background: c.bg, border: `1px solid ${c.border}`,
  };
});

const cancelBtnSx = {
  px: 3, py: 1, backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: '6px', fontWeight: 500,
  textTransform: 'none', minWidth: '100px', height: '40px',
  '&:hover':    { backgroundColor: '#e5e7eb' },
  '&:disabled': { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1, backgroundColor: '#2563eb', color: '#fff',
  borderRadius: '6px', fontWeight: 600, textTransform: 'none',
  minWidth: '160px', height: '40px',
  display: 'flex', alignItems: 'center', gap: 1,
  '&:hover':    { backgroundColor: '#1d4ed8' },
  '&:disabled': { backgroundColor: '#93c5fd' },
};

const releaseBtnSx = {
  px: 3, py: 1, backgroundColor: '#fff7ed', color: '#9a3412',
  border: '1px solid #fdba74', borderRadius: '6px', fontWeight: 600,
  textTransform: 'none', minWidth: '140px', height: '40px',
  display: 'flex', alignItems: 'center', gap: 1,
  '&:hover':    { backgroundColor: '#ffedd5' },
  '&:disabled': { opacity: 0.6 },
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AssignAssetModal
 *
 * Standalone full-screen modal for assigning / re-assigning / releasing an asset.
 * Business logic (payload construction, validation) is delegated to AssetModel.
 * DB writes are delegated to assetService.upsertData().
 *
 * Props:
 *   open        {boolean}
 *   asset       {object}    — the asset record to act on
 *   onClose     {function}
 *   onSave      {function}  — called with the updated asset after a successful save
 *   onError     {function}  — (message, severity) toast callback from the parent
 *   currentUser {object}    — authenticated user from auth context
 *   departments {Array}
 *   users       {Array}
 */
const AssignAssetModal = ({
  open,
  asset        = null,
  onClose,
  onSave,
  onError,
  currentUser,
  departments  = [],
  users        = [],
}) => {

  // ── Form state ────────────────────────────────────────────────────────────
  const [assignedTo,             setAssignedTo]             = useState('');
  const [assignedToName,         setAssignedToName]         = useState('');
  const [assignedDepartmentId,   setAssignedDepartmentId]   = useState('');
  const [assignedDepartmentName, setAssignedDepartmentName] = useState('');
  const [notes,                  setNotes]                  = useState('');
  const [expectedReturnDate,     setExpectedReturnDate]     = useState('');

  // ── UI state ──────────────────────────────────────────────────────────────
  const [loading,        setLoading]        = useState(false);
  const [errors,         setErrors]         = useState({});
  const [submitError,    setSubmitError]    = useState('');
  const [historyItems,   setHistoryItems]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTab,      setActiveTab]      = useState('assign'); // 'assign' | 'history'

  const formBodyRef = useRef(null);

  // ── Derived state ─────────────────────────────────────────────────────────
  const currentAssignedTo     = asset?.assignedTo     || '';
  const currentAssignedToName = asset?.assignedToName || '';
  const isCurrentlyAssigned   = Boolean(currentAssignedTo);

  // ── Seed form + history when modal opens ─────────────────────────────────
  useEffect(() => {
    if (!open || !asset) return;

    setAssignedTo(asset.assignedTo           || '');
    setAssignedToName(asset.assignedToName   || '');
    setAssignedDepartmentId(asset.assignedDepartmentId   || '');
    setAssignedDepartmentName(asset.assignedDepartmentName || '');
    setNotes('');
    setExpectedReturnDate('');
    setErrors({});
    setSubmitError('');
    setActiveTab('assign');

    // History lives on the asset document — read it directly from the prop.
    // If you later move history to a sub-collection, swap this for a service call.
    setHistoryLoading(true);
    try {
      setHistoryItems(getSortedAssignmentHistory(asset));
    } finally {
      setHistoryLoading(false);
    }
  }, [open, asset]);

  // ── Form field handlers ───────────────────────────────────────────────────

 const handleUserChange = (userId) => {
  const user = users.find((u) => u.id === userId);
  setAssignedTo(userId);
  setAssignedToName(user?.displayName || user?.name || '');
  
 
  setAssignedDepartmentId(user?.departmentId || '');
  setAssignedDepartmentName(user?.departmentName || '');
  
  setErrors((prev) => ({ ...prev, assignedTo: '', assignedDepartmentId: '' }));
};

  const handleDeptChange = (deptId) => {
    const dept = departments.find((d) => d.id === deptId);
    setAssignedDepartmentId(deptId);
    setAssignedDepartmentName(dept?.departmentName || dept?.name || '');
  };

  // ── Validate ──────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const errs = validateAssignmentForm({ assignedTo, assignedDepartmentId });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [assignedTo, assignedDepartmentId]);

  // ── Submit: Assign / Re-assign ────────────────────────────────────────────
  const handleAssign = useCallback(async () => {
    if (!validate()) return;

    setLoading(true);
    setSubmitError('');

    const { payload, entryType } = buildAssignPayload({
      asset,
      assignedTo,
      assignedToName,
      assignedDepartmentId,
      assignedDepartmentName,
      notes,
      expectedReturnDate,
      currentUser,
    });

    try {
      await assetService.upsertData(asset.id, payload);
      if (onSave)  onSave({ ...asset, ...payload });
      if (onError) onError(
        getAssignmentSuccessMessage(entryType, assignedToName),
        'success',
      );
      onClose();
    } catch (err) {
      console.error('Assignment failed:', err);
      setSubmitError(err.message || 'Failed to save assignment. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [
    validate, asset, assignedTo, assignedToName,
    assignedDepartmentId, assignedDepartmentName,
    notes, expectedReturnDate, currentUser,
    onSave, onError, onClose,
  ]);

  // ── Release (unassign) ────────────────────────────────────────────────────
  const handleRelease = useCallback(async () => {
    if (!isCurrentlyAssigned) return;

    setLoading(true);
    setSubmitError('');

    const payload = buildReleasePayload({ asset, notes, currentUser });

    try {
await assetService.upsertData(asset.id, payload);
      if (onSave)  onSave({ ...asset, ...payload });
      if (onError) onError(
        getAssignmentSuccessMessage(ASSIGNMENT_TYPES.RELEASED),
        'success',
      );
      onClose();
    } catch (err) {
      console.error('Release failed:', err);
      setSubmitError(err.message || 'Failed to release asset. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isCurrentlyAssigned, asset, notes, currentUser, onSave, onError, onClose]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  if (!asset) return null;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          width: '100vw', height: '100vh',
          maxWidth: '100vw', maxHeight: '100vh',
          margin: 0, boxSizing: 'border-box',
        },
      }}
    >
      {/* ── APP BAR ──────────────────────────────────────────────────────── */}
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} disabled={loading}>
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              Assign Asset
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {asset.assetId} — {asset.assetName}
            </Typography>
          </Box>

          {/* Tab switcher */}
          <Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
            {[
              { key: 'assign',  label: 'Assign',  icon: <AssignmentIndIcon sx={{ fontSize: '1rem' }} /> },
              { key: 'history', label: 'History', icon: <HistoryIcon       sx={{ fontSize: '1rem' }} /> },
            ].map(({ key, label, icon }) => (
              <Button
                key={key}
                onClick={() => setActiveTab(key)}
                startIcon={icon}
                sx={{
                  color:       activeTab === key ? '#fff' : 'rgba(255,255,255,0.65)',
                  fontWeight:  activeTab === key ? 700   : 400,
                  textTransform: 'none', fontSize: '0.8125rem',
                  borderBottom: activeTab === key
                    ? '2px solid #fff'
                    : '2px solid transparent',
                  borderRadius: 0, px: 1.5,
                }}
              >
                {label}
                {key === 'history' && historyItems.length > 0 && (
                  <Chip
                    label={historyItems.length}
                    size="small"
                    sx={{
                      ml: 0.75, height: 18, fontSize: '0.65rem',
                      bgcolor: 'rgba(255,255,255,0.25)', color: '#fff',
                    }}
                  />
                )}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── SCROLLABLE BODY ───────────────────────────────────────────────── */}
      <Box
        ref={formBodyRef}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: '#f1f5f9', py: 4 }}
      >
        <Container maxWidth="md">
          <Stack spacing={3}>

            {/* ── Current Assignment Status ─────────────────────────────── */}
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Current Assignment Status</SectionTitle>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>

                  {/* Current custodian */}
                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: isCurrentlyAssigned ? '#eff6ff' : '#f0fdf4',
                    border: `1px solid ${isCurrentlyAssigned ? '#bfdbfe' : '#bbf7d0'}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Current Custodian
                    </Typography>
                    <Typography sx={{
                      fontSize: '1rem', fontWeight: 700,
                      color: isCurrentlyAssigned ? '#1e40af' : '#166534',
                    }}>
                      {isCurrentlyAssigned ? currentAssignedToName : 'Unassigned'}
                    </Typography>
                    {asset.assignedDepartmentName && (
                      <Typography sx={{ fontSize: '0.8125rem', color: '#6b7280', mt: 0.25 }}>
                        {asset.assignedDepartmentName}
                      </Typography>
                    )}
                    {asset.assignedOn && (
                      <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.5 }}>
                        Since: {formatDate(asset.assignedOn)}
                      </Typography>
                    )}
                  </Box>

                  {/* Asset status */}
                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Asset Status
                    </Typography>
                    <Chip
                      label={asset.status || 'Unknown'}
                      size="small"
                      sx={{
                        fontWeight: 600, fontSize: '0.8125rem',
                        bgcolor:
                          asset.status === 'Assigned'  ? '#dbeafe' :
                          asset.status === 'Available' ? '#d1fae5' : '#fef3c7',
                        color:
                          asset.status === 'Assigned'  ? '#1e40af' :
                          asset.status === 'Available' ? '#065f46' : '#92400e',
                      }}
                    />
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 1 }}>
                      Location: {asset.locationName || asset.locationCode || '—'}
                    </Typography>
                  </Box>

                  {/* History count */}
                  <Box sx={{
                    flex: 1, minWidth: 220, p: 2, borderRadius: 2,
                    background: '#f8fafc', border: '1px solid #e2e8f0',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 600, color: '#6b7280',
                      textTransform: 'uppercase', mb: 0.5,
                    }}>
                      Assignment History
                    </Typography>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>
                      {historyItems.length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {historyItems.length === 1 ? 'event recorded' : 'events recorded'}
                    </Typography>
                  </Box>

                </Box>
              </CardContent>
            </SectionCard>

            {/* ══════════════════════════════════════════════════════════════
                TAB: ASSIGN
            ══════════════════════════════════════════════════════════════ */}
            {activeTab === 'assign' && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>
                    {isCurrentlyAssigned ? 'Re-assign Asset' : 'Assign Asset'}
                  </SectionTitle>

                  {/* Re-assignment notice */}
                  {isCurrentlyAssigned && (
                    <Box sx={{
                      p: 2, mb: 2, borderRadius: 2,
                      background: '#faf5ff', border: '1px solid #e9d5ff',
                      display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                      <SwapHorizIcon sx={{ color: '#7c3aed', fontSize: '1.1rem' }} />
                      <Typography sx={{ fontSize: '0.8125rem', color: '#6b21a8' }}>
                        Selecting a different user will create a{' '}
                        <strong>re-assignment</strong> event in the history.
                        The previous custodian will be recorded.
                      </Typography>
                    </Box>
                  )}

               {/* User + Department */}
<FormRow>
  <FormGroup>
    <FieldLabel>
      Assign To (User) <span style={{ color: '#ef4444' }}>*</span>
    </FieldLabel>
    <FormControl fullWidth size="small" error={Boolean(errors.assignedTo)}>
      <SearchableDropdown
        id="assignedTo"
        name="assignedTo"
        value={assignedTo}
        onChange={(e) => handleUserChange(e.target.value)}
        options={users}
        placeholder="Search by name or email..."
        // Use email + department as the label for better search visibility
        getOptionLabel={(option) => 
          `${option.displayName || option.name || option.email} — ${option.departmentName || 'General Staff'}`
        }
        getOptionValue={(option) => option.id}
      />
      {errors.assignedTo && (
        <Typography sx={{ fontSize: '0.75rem', color: '#dc2626', mt: 0.5 }}>
          {errors.assignedTo}
        </Typography>
      )}
    </FormControl>
  </FormGroup>

 
  <FormGroup>
    <FieldLabel>Department</FieldLabel>
    <TextField
      size="small"
      fullWidth
      disabled
      value={assignedDepartmentName || 'Select a user first...'}
      sx={{
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: '#374151', 
          backgroundColor: '#f3f4f6',
        },
      }}
    />
  </FormGroup>
</FormRow>
                   

                  {/* Expected return date */}
                  <FormRow>
                    <FormGroup>
                      <FieldLabel>Expected Return Date (Optional)</FieldLabel>
                      <TextField
                        type="date" size="small" fullWidth
                        value={expectedReturnDate}
                        onChange={(e) => setExpectedReturnDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      />
                    </FormGroup>
                  </FormRow>

                  {/* Notes */}
                  <FormRow>
                    <FormGroup>
                      <FieldLabel>Notes / Reason (Optional)</FieldLabel>
                      <TextField
                        multiline rows={3} size="small" fullWidth
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="e.g. Assigned for project X, field visit, temporary replacement…"
                        inputProps={{ maxLength: 1000 }}
                        helperText={`${notes.length}/1000`}
                      />
                    </FormGroup>
                  </FormRow>

                  {/* Release section */}
                  {isCurrentlyAssigned && (
                    <>
                      <Divider sx={{ my: 3 }}>
                        <Typography sx={{
                          fontSize: '0.75rem', color: '#9ca3af',
                          fontWeight: 600, textTransform: 'uppercase',
                        }}>
                          or release this asset
                        </Typography>
                      </Divider>

                      <Box sx={{
                        p: 2, borderRadius: 2,
                        background: '#fff7ed', border: '1px solid #fdba74',
                      }}>
                        <Typography sx={{
                          fontSize: '0.8125rem', color: '#9a3412',
                          fontWeight: 600, mb: 0.5,
                        }}>
                          Release from current custodian
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: '#92400e', mb: 1.5 }}>
                          This will unassign the asset from{' '}
                          <strong>{currentAssignedToName}</strong> and set the status
                          back to <strong>Available</strong>. A release event will be
                          recorded in the history.
                        </Typography>
                        <Button
                          onClick={handleRelease}
                          disabled={loading}
                          startIcon={<PersonOffIcon />}
                          sx={releaseBtnSx}
                        >
                          {loading
                            ? <CircularProgress size={14} color="inherit" />
                            : 'Release Asset'}
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </SectionCard>
            )}

            {/* ══════════════════════════════════════════════════════════════
                TAB: HISTORY
            ══════════════════════════════════════════════════════════════ */}
            {activeTab === 'history' && (
              <SectionCard variant="outlined">
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle>Assignment History</SectionTitle>

                  {/* Loading */}
                  {historyLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} />
                    </Box>
                  )}

                  {/* Empty */}
                  {!historyLoading && historyItems.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>
                      <HistoryIcon sx={{ fontSize: '3rem', opacity: 0.3, mb: 1 }} />
                      <Typography sx={{ fontWeight: 600 }}>
                        No assignment history yet
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                        Assignment events will appear here once this asset is assigned.
                      </Typography>
                    </Box>
                  )}

                  {/* History list */}
                  {!historyLoading && historyItems.length > 0 && (
                    <Stack spacing={2}>
                      {historyItems.map((entry, idx) => {
                        const typeInfo = getAssignmentTypeConfig(entry.type);
                        return (
                          <HistoryEntry
                            key={entry.id || idx}
                            entrytype={entry.type || ASSIGNMENT_TYPES.ASSIGNED}
                          >
                            {/* Avatar */}
                            <Avatar
                              sx={{
                                width: 40, height: 40, flexShrink: 0,
                                bgcolor:  typeInfo.border,
                                color:    typeInfo.text,
                                fontSize: '0.875rem', fontWeight: 700,
                              }}
                            >
                              {entry.type === ASSIGNMENT_TYPES.RELEASED
                                ? <PersonOffIcon sx={{ fontSize: '1.1rem' }} />
                                : getInitials(entry.assignedToName || '')}
                            </Avatar>

                            {/* Content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {/* Type chip + date + "latest" badge */}
                              <Box sx={{
                                display: 'flex', alignItems: 'center',
                                gap: 1, flexWrap: 'wrap', mb: 0.5,
                              }}>
                                <Chip
                                  label={typeInfo.label}
                                  size="small"
                                  sx={{
                                    bgcolor:    typeInfo.bg,
                                    color:      typeInfo.text,
                                    fontWeight: 700, fontSize: '0.7rem',
                                    border: `1px solid ${typeInfo.border}`,
                                  }}
                                />
                                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                  {formatDate(entry.assignedOn)}
                                </Typography>
                                {idx === 0 && (
                                  <Chip
                                    label="Latest" size="small"
                                    sx={{
                                      bgcolor: '#fef9c3', color: '#713f12',
                                      fontSize: '0.65rem', fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Main content rows */}
                              {entry.type !== ASSIGNMENT_TYPES.RELEASED ? (
                                <>
                                  <Typography sx={{
                                    fontWeight: 700, fontSize: '0.9375rem', color: '#1f2937',
                                  }}>
                                    {entry.assignedToName || '—'}
                                  </Typography>
                                  {entry.assignedDepartmentName && (
                                    <Typography sx={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                                      {entry.assignedDepartmentName}
                                    </Typography>
                                  )}
                                  {entry.previousAssignedToName && (
                                    <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>
                                      Replaced: {entry.previousAssignedToName}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Typography sx={{
                                    fontWeight: 700, fontSize: '0.9375rem', color: '#9a3412',
                                  }}>
                                    Asset Released
                                  </Typography>
                                  {entry.releasedFromUserName && (
                                    <Typography sx={{ fontSize: '0.8125rem', color: '#6b7280' }}>
                                      From: {entry.releasedFromUserName}
                                    </Typography>
                                  )}
                                </>
                              )}

                              {/* Meta row */}
                              <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  By: <strong>{entry.assignedByName || '—'}</strong>
                                </Typography>
                                {entry.expectedReturnDate && (
                                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    Return by:{' '}
                                    <strong>
                                      {new Date(entry.expectedReturnDate)
                                        .toLocaleDateString('en-IN')}
                                    </strong>
                                  </Typography>
                                )}
                              </Box>

                              {/* Notes */}
                              {entry.notes && (
                                <Box sx={{
                                  mt: 1, p: 1,
                                  bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1,
                                }}>
                                  <Typography sx={{
                                    fontSize: '0.8125rem', color: '#374151', fontStyle: 'italic',
                                  }}>
                                    "{entry.notes}"
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </HistoryEntry>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </SectionCard>
            )}

          </Stack>
        </Container>
      </Box>

      {/* ── STICKY FOOTER ────────────────────────────────────────────────── */}
      <Box sx={{
        flexShrink: 0, width: '100%', height: '72px',
        borderTop: '1px solid #e2e8f0', backgroundColor: '#fff',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
        display: 'flex', alignItems: 'center',
        justifyContent: 'flex-end', gap: 1.5, px: 3,
      }}>
        <Button onClick={onClose} disabled={loading} sx={cancelBtnSx}>
          Cancel
        </Button>
        {activeTab === 'assign' && (
          <Button onClick={handleAssign} disabled={loading} sx={submitBtnSx}>
            {loading && <CircularProgress size={16} color="inherit" />}
            {loading
              ? 'Saving…'
              : isCurrentlyAssigned ? 'Re-assign Asset' : 'Assign Asset'}
          </Button>
        )}
      </Box>

      {/* ── ERROR SNACKBAR ────────────────────────────────────────────────── */}
      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSubmitError('')}
          severity="error"
          variant="filled"
          icon={<ErrorOutlineIcon />}
          sx={{ width: '100%', minWidth: 400, fontSize: '0.9rem', fontWeight: 500 }}
        >
          {submitError}
        </Alert>
      </Snackbar>

    </Dialog>
  );
};

export default AssignAssetModal;